import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Slide, styled } from "@mui/material";
import { commonBoxShadowStyleByOpacity } from "fe-common/ui/boxShadow";
import CloseIconButton from "fe-common/ui/icons/closeIconButton";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { ColoredRoundActionButton } from "fe-common/ui/coloredRoundActionButton";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { UserStorageKey } from "fe-web-components/userStorage/userStorageKey";
import { writeToGlobalStorage } from "fe-web-components/userStorage/globalUserStorage";
import clsx from "clsx";
import { useLocaleContext } from "fe-common/locale/localeContext";

//region [[ Styles ]]

const NewVersionPopoverView = styled("div")(({ theme }) => ({
  ...commonBoxShadowStyleByOpacity("0.19"),
  position: "fixed",
  height: "55px",
  background: "white",
  display: "flex",
  flexFlow: "row",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottomRightRadius: "10px",
  borderBottomLeftRadius: "10px",
  padding: "16px 2%",
  width: "100%",
  left: 0,
  [theme.breakpoints.down("sm")]: {
    padding: "11px",
    paddingTop: "25px",
    height: "75px",
  },
  backgroundColor: KahunColorMap.Accent,
}));

const NewVersionText = styled("span")(({ theme }) => ({
  fontSize: "13px",
  lineHeight: "14px",
  color: KahunColorMap.White,
  fontWeight: 500,
  padding: "0 11px",
  [theme.breakpoints.up("md")]: {
    width: "50%",
  },
}));

const CloseButton = styled(CloseIconButton)({
  "&.close-button": {
    position: "static",
    borderRadius: "50%",
    height: "20px",
    width: "20px",
    background: KahunColorMap.White,
    color: "white",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& svg": {
      height: "10px",
      width: "10px",
    },
  },
});

const Left = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    justifyContent: "flex-start",
  },
}));

const UpdateButton = styled(ColoredRoundActionButton)({
  minWidth: "64px",
  height: "24px",
  fontSize: "12px",
  padding: "8px 5px",
  color: KahunColorMap.Accent,
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface NewVersionPopoverProps {
  className?: string;
  version: string;
  onReloadClicked?: () => void;
  /**
   * When set to true clicking the "update" will remain the query params as they are
   * the default behaviour is to remove "?shareId=xxx" to avoid re-settings
   * shared agent cases after modifying them
   * */
  keepQueryParams?: boolean;
  locale?: string;
  translate?: (string) => string;
  direction?: "up" | "down";
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const NewVersionPopover = ({
  version,
  translate,
  ...props
}: NewVersionPopoverProps) => {
  const [reloadIsNeeded, setReloadIsNeeded] = useState(
    localStorage.getItem(UserStorageKey.ReloadNeeded) === "true",
  );

  const { locale } = useLocaleContext();

  let NEW_VERSION_AVAILABLE_TEXT = "NEW_VERSION_AVAILABLE_TEXT",
    UPDATE_BUTTON_TEXT = "UPDATE_BUTTON_TEXT";

  // If we use the translate platform use it
  if (translate) {
    NEW_VERSION_AVAILABLE_TEXT = translate(NEW_VERSION_AVAILABLE_TEXT);
    UPDATE_BUTTON_TEXT = translate(UPDATE_BUTTON_TEXT);
  } else if (locale === "he") {
    // TODO: use trasnlate everywhere (in agent and others also)
    NEW_VERSION_AVAILABLE_TEXT = "גרסא חדשה זמינה לעדכון";
    UPDATE_BUTTON_TEXT = "עדכן";
  } else {
    NEW_VERSION_AVAILABLE_TEXT = "A new version is available";
    UPDATE_BUTTON_TEXT = "Update";
  }

  const [showPopover, setShowPopover] = useState(reloadIsNeeded);

  const intervalRef = React.useRef(null);

  // If reload-is-needed changed, change accordingly
  useEffect(() => {
    setShowPopover(reloadIsNeeded);
    if (reloadIsNeeded) {
      evt({
        category: CATEGORY.NEW_VERSION_POPOVER,
        action: ACTION.DISPLAYED,
        label: `current client version: ${version}}`,
      });
    }
  }, [reloadIsNeeded]);

  // Set interval to check for the storage flag
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      setReloadIsNeeded(
        localStorage.getItem(UserStorageKey.ReloadNeeded) === "true",
      );
    }, 700);
    return () => clearInterval(intervalRef.current);
  }, [setReloadIsNeeded]);

  const handleReloadClicked = () => {
    if (props.onReloadClicked) props.onReloadClicked();

    evt({
      category: CATEGORY.NEW_VERSION_POPOVER,
      action: ACTION.UPDATE,
      label: version,
    });
    writeToGlobalStorage(UserStorageKey.PreventStartOver, "true");
    writeToGlobalStorage(UserStorageKey.ReloadNeeded, "false");
    // Clear shareId so the shared case won't reload
    // (since reloading a page with "?shareId=XX" will cause the XX case to load
    // removing all locally-user-modified entries)
    const searchParams = new URLSearchParams(window.location.search);
    if (!props.keepQueryParams) {
      searchParams.delete("shareId");
    }
    // Give the storage some time to finish clearing the flag before reloading
    // so we won't get the message again after reload
    setTimeout(
      () =>
        window.location.replace(`${window.location.pathname}?${searchParams}`),
      200,
    );
  };

  const handleDismissUpdate = useCallback(() => {
    evt({
      category: CATEGORY.NEW_VERSION_POPOVER,
      action: ACTION.CLOSE,
      label: version,
    });

    setShowPopover(false);
  }, [setShowPopover]);

  return (
    <Slide
      in={showPopover}
      mountOnEnter
      unmountOnExit
      direction={props.direction || "down"}
    >
      <NewVersionPopoverView
        className={clsx(props.className, "new-version-popover")}
      >
        <Left>
          <CloseButton
            className={"close-button"}
            onClick={handleDismissUpdate}
          />
          <NewVersionText className={"new-version-text"}>
            {NEW_VERSION_AVAILABLE_TEXT}
          </NewVersionText>
        </Left>
        <UpdateButton
          className={"update-button"}
          label={UPDATE_BUTTON_TEXT}
          onClick={handleReloadClicked}
        />
      </NewVersionPopoverView>
    </Slide>
  );
};
